import { useCallback, useState } from "react";
import {
  type ModalActions,
  type ModalState,
  ModalType,
} from "@/features/study-group-events/type";
import type { EventApiResponse } from "@/lib/humhub-api/types";

export interface ExtendedModalActions extends ModalActions {}

export const useEventModalState = () => {
  const [state, setState] = useState<ModalState>({
    activeModal: null,
    selectedEvent: null,
    selectedEventApiData: null,
    selectedDate: null,
    selectedEvents: [],
    selectedDeleteScope: null,
    previousModalState: null,
  });

  const saveCurrentStateAsPrevious = useCallback((currentState: ModalState) => {
    const { previousModalState: _ignored, ...stateWithoutPrevious } =
      currentState;
    return {
      ...currentState,
      previousModalState: { ...stateWithoutPrevious },
    };
  }, []);

  const actions: ExtendedModalActions = {
    openEventDetail: useCallback(
      (event: EventApiResponse) => {
        setState((prev) => {
          if (prev.activeModal === ModalType.DAY_EVENTS) {
            return {
              ...saveCurrentStateAsPrevious(prev),
              activeModal: ModalType.EVENT_DETAIL,
              selectedEvent: event,
              selectedEventApiData: event,
            };
          }

          return {
            ...prev,
            activeModal: ModalType.EVENT_DETAIL,
            selectedEvent: event,
            selectedEventApiData: event,
            selectedDate: null,
            selectedEvents: [],
          };
        });
      },
      [saveCurrentStateAsPrevious],
    ),

    openEventCreate: useCallback((date?: Date) => {
      setState((prev) => ({
        ...prev,
        activeModal: ModalType.EVENT_CREATE,
        selectedEvent: null,
        selectedEventApiData: null,
        selectedDate: date || null,
        selectedEvents: [],
      }));
    }, []),

    openEventEdit: useCallback(
      (event: EventApiResponse, eventApiData: EventApiResponse) => {
        setState((prev) => {
          if (prev.activeModal === ModalType.EVENT_DETAIL) {
            return {
              ...saveCurrentStateAsPrevious(prev),
              activeModal: ModalType.EVENT_EDIT,
              selectedEvent: event,
              selectedEventApiData: eventApiData,
              selectedDate: null,
              selectedEvents: [],
              formData: null,
            };
          }

          return {
            ...prev,
            activeModal: ModalType.EVENT_EDIT,
            selectedEvent: event,
            selectedEventApiData: eventApiData,
            selectedDate: null,
            selectedEvents: [],
          };
        });
      },
      [saveCurrentStateAsPrevious],
    ),

    openEventDuplicate: useCallback(
      (event: EventApiResponse, eventApiData: EventApiResponse) => {
        setState((prev) => {
          if (prev.activeModal === ModalType.EVENT_DETAIL) {
            return {
              ...saveCurrentStateAsPrevious(prev),
              activeModal: ModalType.EVENT_DUPLICATE,
              selectedEvent: event,
              selectedEventApiData: eventApiData,
              selectedDate: null,
              selectedEvents: [],
              formData: null,
            };
          }

          return {
            ...prev,
            activeModal: ModalType.EVENT_DUPLICATE,
            selectedEvent: event,
            selectedEventApiData: eventApiData,
            selectedDate: null,
            selectedEvents: [],
          };
        });
      },
      [saveCurrentStateAsPrevious],
    ),

    openEventDelete: useCallback(
      (event: EventApiResponse, eventApiData: EventApiResponse, scope) => {
        setState((prev) => {
          if (prev.activeModal === ModalType.EVENT_DETAIL) {
            return {
              ...saveCurrentStateAsPrevious(prev),
              activeModal: ModalType.EVENT_DELETE,
              selectedEvent: event,
              selectedEventApiData: eventApiData,
              selectedDate: null,
              selectedEvents: [],
              selectedDeleteScope: scope ?? null,
            };
          }

          return {
            ...prev,
            activeModal: ModalType.EVENT_DELETE,
            selectedEvent: event,
            selectedEventApiData: eventApiData,
            selectedDate: null,
            selectedEvents: [],
            selectedDeleteScope: scope ?? null,
          };
        });
      },
      [saveCurrentStateAsPrevious],
    ),

    openEventPublish: useCallback(
      (event: EventApiResponse, eventApiData: EventApiResponse) => {
        setState((prev) => ({
          ...prev,
          activeModal: ModalType.EVENT_PUBLISH,
          selectedEvent: event,
          selectedEventApiData: eventApiData,
          selectedDate: null,
          selectedEvents: [],
        }));
      },
      [],
    ),

    openDayEvents: useCallback((date: Date, events: EventApiResponse[]) => {
      setState((prev) => ({
        ...prev,
        activeModal: ModalType.DAY_EVENTS,
        selectedEvent: null,
        selectedEventApiData: null,
        selectedDate: date,
        selectedEvents: events,
        formData: null,
      }));
    }, []),

    closeModal: useCallback((options?: { reset?: boolean }) => {
      setState((prev) => {
        if (options?.reset) {
          return {
            activeModal: null,
            selectedEvent: null,
            selectedEventApiData: null,
            selectedDate: null,
            selectedEvents: [],
            selectedDeleteScope: null,
            previousModalState: null,
          };
        }

        if (
          prev.activeModal === ModalType.EVENT_DETAIL &&
          prev.previousModalState &&
          prev.previousModalState.activeModal === ModalType.DAY_EVENTS
        ) {
          return {
            ...prev.previousModalState,
            previousModalState: null,
          };
        }

        if (
          prev.activeModal === ModalType.EVENT_EDIT &&
          prev.previousModalState &&
          prev.previousModalState.activeModal === ModalType.EVENT_DETAIL
        ) {
          return {
            ...prev.previousModalState,
            previousModalState: null,
          };
        }

        if (
          prev.activeModal === ModalType.EVENT_DUPLICATE &&
          prev.previousModalState &&
          prev.previousModalState.activeModal === ModalType.EVENT_DETAIL
        ) {
          return {
            ...prev.previousModalState,
            previousModalState: null,
          };
        }

        if (
          prev.activeModal === ModalType.EVENT_DELETE &&
          prev.previousModalState &&
          prev.previousModalState.activeModal === ModalType.EVENT_DETAIL
        ) {
          return {
            ...prev.previousModalState,
            previousModalState: null,
          };
        }

        return {
          ...prev,
          activeModal: null,
          selectedEvent: null,
          selectedEventApiData: null,
          selectedDate: null,
          selectedEvents: [],
          selectedDeleteScope: null,
          previousModalState: null,
        };
      });
    }, []),
  };

  return {
    state,
    actions,
  };
};
